if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      
      // Always set up arrow handlers, even without thumbnails
      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        // Listen to slideChanged immediately for arrow updates (no debounce)
        this.elements.viewer.addEventListener('slideChanged', (event) => {
          if (event.detail.currentElement) {
            this.updateMobileArrows(event.detail.currentElement);
          }
        });
        
        // Initialize mobile arrow visibility
        this.initializeMobileArrows();
        
        // Add click handlers for mobile arrows
        this.setupMobileArrowHandlers();
      }
      
      if (!this.elements.thumbnails) return;

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
    }
    
    setupMobileArrowHandlers() {
      const prevButton = this.querySelector('.wd-mobile-arrow--prev');
      const nextButton = this.querySelector('.wd-mobile-arrow--next');
      
      if (prevButton) {
        prevButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.navigateToAdjacentSlide('prev');
        });
      }
      
      if (nextButton) {
        nextButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.navigateToAdjacentSlide('next');
        });
      }
    }
    
    navigateToAdjacentSlide(direction) {
      const slider = this.elements.viewer;
      if (!slider) return;
      
      const slides = slider.querySelectorAll('.product__media-item');
      const activeSlide = slider.querySelector('.product__media-item.is-active');
      
      if (!activeSlide || slides.length < 2) return;
      
      const currentIndex = Array.from(slides).indexOf(activeSlide);
      let targetIndex;
      
      if (direction === 'next') {
        targetIndex = currentIndex + 1;
        if (targetIndex >= slides.length) return;
      } else {
        targetIndex = currentIndex - 1;
        if (targetIndex < 0) return;
      }
      
      const targetSlide = slides[targetIndex];
      const mediaId = targetSlide.dataset.mediaId;
      
      if (mediaId) {
        this.setActiveMedia(mediaId, false, true);
      }
    }
    
    initializeMobileArrows() {
      const slides = this.elements.viewer?.querySelectorAll('.product__media-item');
      const activeSlide = this.elements.viewer?.querySelector('.product__media-item.is-active');
      
      if (slides && activeSlide) {
        this.updateMobileArrows(activeSlide);
      }
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      
      // Arrow visibility is updated immediately via separate event listener (no debounce)
    }
    
    updateMobileArrows(currentElement) {
      const slider = this.elements.viewer;
      const prevButton = this.querySelector('.wd-mobile-arrow--prev');
      const nextButton = this.querySelector('.wd-mobile-arrow--next');
      
      if (!prevButton || !nextButton || !slider) return;
      
      const slides = slider.querySelectorAll('.product__media-item');
      const currentIndex = Array.from(slides).indexOf(currentElement);
      
      // Show/hide previous arrow using opacity/visibility for instant toggle
      if (currentIndex <= 0) {
        prevButton.style.opacity = '0';
        prevButton.style.visibility = 'hidden';
        prevButton.style.pointerEvents = 'none';
      } else {
        prevButton.style.opacity = '1';
        prevButton.style.visibility = 'visible';
        prevButton.style.pointerEvents = 'auto';
      }
      
      // Show/hide next arrow using opacity/visibility for instant toggle
      if (currentIndex >= slides.length - 1) {
        nextButton.style.opacity = '0';
        nextButton.style.visibility = 'hidden';
        nextButton.style.pointerEvents = 'none';
      } else {
        nextButton.style.opacity = '1';
        nextButton.style.visibility = 'visible';
        nextButton.style.pointerEvents = 'auto';
      }
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);
      
      // Update mobile arrows when media changes
      this.updateMobileArrows(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
